import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Typography,
  Divider,
  Skeleton,
  Box,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import { updateVisit, addNewBill } from "../API/BillingApi";
import AuthContext from "../Context/AuthProvider";
import { showLoadingImage } from "../Utility/Utility"; // Import showLoadingImage

const PatientVisitPopup = ({ open, onClose, onVisitSelected }) => {
  const [patientVisits, setPatientVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false); // This state controls the loading overlay
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { auth } = useContext(AuthContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchPatientVisits = useCallback(async () => {
    if (!open || !auth?.selectedFacility?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const facilityId = auth.selectedFacility.id;
      const to = new Date().toISOString().split("T")[0];
      const from = new Date();
      from.setDate(from.getDate() - 7);
      const fromDate = from.toISOString().split("T")[0];

      const statusList = ["Scheduled", "InProgress", "CheckedIn"]; // Defined statusList

      const result = await updateVisit(auth, facilityId, statusList, fromDate, to);

      if (result.isSuccess && Array.isArray(result.data)) {
        const formattedData = result.data.map((visit) => ({
          ...visit,
          id: visit.id || visit.visitId,
          patientName:
            `${visit.patientFirstName || ""} ${
              visit.patientLastName || ""
            }`.trim() || "N/A",
          gender: visit.patientGender || "N/A",
          age: visit.patientAge || "N/A",
          phoneNo: visit.patientMobileNo || "N/A",
          displayId: visit.displayId || visit.visitNo || visit.visitId,
          billingStatus: visit.billId ? "Billed" : "Unbilled",
          billId: visit.billId || null,
        }));

        setPatientVisits(formattedData);
        setFilteredVisits(formattedData);
      } else {
        setError(result.data?.message || "Failed to load patient visits.");
      }
    } catch (error) {
      setError("An error occurred while fetching patient visits.");
    } finally {
      setIsLoading(false);
    }
  }, [open, auth]);

  useEffect(() => {
    fetchPatientVisits();
  }, [fetchPatientVisits]);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredVisits(patientVisits);
      return;
    }

    const lowerCaseQuery = searchQuery.toLowerCase();
    setFilteredVisits(
      patientVisits.filter(
        (visit) =>
          visit.patientName.toLowerCase().includes(lowerCaseQuery) ||
          visit.displayId.toLowerCase().includes(lowerCaseQuery) ||
          visit.phoneNo.includes(searchQuery) ||
          visit.visitStatus.toLowerCase().includes(lowerCaseQuery)
      )
    );
  }, [searchQuery, patientVisits]);

  const columns = [
    { field: "displayId", headerName: "Visit ID", width: 150 },
    { field: "patientName", headerName: "Patient Name", width: 200 },
    { field: "visitStatus", headerName: "Visit Status", width: 200 },
    {
      field: "createdOn",
      headerName: "Visit Time",
      width: 250,
      renderCell: (params) => {
        const date = new Date(params.row.createdOn);
        const day = date.getDate();
        const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
        const year = date.getFullYear();
        const time = date.toLocaleString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
        return `${day} ${month} ${year}, ${time}`;
      },
    },
  ];

  const handleSelectVisit = async () => {
    if (!selectedVisit) return;

    setBillingLoading(true); // Start loading when select is clicked
    setError(null);

    try {
      const visitId = selectedVisit.id;
      const result = await addNewBill(auth, visitId);

      if (result.isSuccess) {
        const billInfo = result.data;
        
     
        onVisitSelected(billInfo);
        onClose();
      } else {
        setError(result.data?.message || "Billing update failed.");
      }
    } catch (error) {
      setError("An error occurred while updating billing.");
    } finally {
      setBillingLoading(false); // Stop loading after operation
    }
  };

  const renderVisitCards = () => {
    if (isLoading) {
      return (
        <Box sx={{ p: 1 }}>
          {Array.from(new Array(5)).map((_, index) => (
            <Card key={index} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent>
                <Skeleton variant="text" width="60%" height={25} />
                <Divider sx={{ my: 1 }} />
                <Skeleton variant="text" width="80%" height={20} />
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="90%" height={20} />
              </CardContent>
            </Card>
          ))}
        </Box>
      );
    }

    if (!isLoading && filteredVisits.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No patient visits available.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ px: 1 }}>
        {filteredVisits.map((visit) => (
          <Card
            key={visit.id}
            variant="outlined"
            onClick={() => setSelectedVisit(visit)}
            sx={{
              mb: 2,
              borderRadius: 2,
              cursor: "pointer",
              borderColor: selectedVisit?.id === visit.id ? theme.palette.secondary.main : 'divider',
              borderWidth: selectedVisit?.id === visit.id ? 2 : 1,
              backgroundColor: visit.billingStatus === "Billed" ? theme.palette.action.disabledBackground : 'inherit',
              opacity: visit.billingStatus === "Billed" ? 0.7 : 1,
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight="bold">
                Visit ID: {visit.displayId}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" gutterBottom>
                Patient: {visit.patientName}
              </Typography>
              <Typography variant="body2">
                Status: {visit.visitStatus}
              </Typography>
              <Typography variant="body2">
                Visit Time: {new Date(visit.createdOn).toLocaleString("en-US", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                }).replace(/,/g, '').toUpperCase()}
              </Typography>
              <Typography variant="body2">
                Billing Status: {visit.billingStatus}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Typography variant="h6">Select Patient Visit</Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by Name, Visit ID, or Phone No..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: isMobile ? "100%" : "40%" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {error ? (
          <p style={{ color: "red", textAlign: 'center' }}>{error}</p>
        ) : (
          isMobile ? (
            renderVisitCards()
          ) : (
            <DataGrid
              rows={filteredVisits}
              columns={columns}
              loading={isLoading}
              localeText={{
                noRowsLabel: "No visits available.",
              }}
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              initialState={{
                density: "compact",
                pagination: {
                  paginationModel: { pageSize: 5 },
                },
              }}
              sx={{
                width: "100%",
                "& .sticky-column": {
                  position: "sticky",
                  right: 0,
                  backgroundColor: "white",
                  zIndex: 100,
                },
                "& .billed-row": {
                  backgroundColor: theme.palette.action.disabledBackground,
                  opacity: 0.7,
                },
                "& .unbilled-row": {
                  backgroundColor: theme.palette.background.paper,
                  opacity: 1,
                },
              }}
              pageSizeOptions={[5, 10, 20]}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              onRowClick={(params) => setSelectedVisit(params.row)}
              getRowClassName={(params) =>
                params.row.billingStatus === "Billed" ? "billed-row" : "unbilled-row"
              }
            />
          )
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSelectVisit}
          disabled={!selectedVisit || selectedVisit.billingStatus === "Billed" || billingLoading}
        >
          {billingLoading ? "Loading..." : "Select"}
        </Button>
      </DialogActions>

      {/* Loading GIF overlay, similar to AddServiceDialog */}
      {billingLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301, 
          }}
        >
          {showLoadingImage()}
        </div>
      )}
    </Dialog>
  );
};

export default PatientVisitPopup;